import React from 'react';
import ONCRP_TOOLS_QP_Card from './OncoRiskCard';
import { KeyValue } from './CommonComponents';

const toTitle = (s) => {
    if (!s || typeof s !== 'string') return 'N/A';
    return s
        .toLowerCase()
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
};

const prettyPhase = (phase) => {
    if (!phase) return 'N/A';
    // Examples: "PHASE3", "PHASE2", "PHASE1, PHASE2"
    const parts = String(phase)
        .split(',')
        .map((p) => p.trim().replace(/^PHASE\s*/, '').replace(/^PHASE/, ''))
        .map((p) => p.replace(/\s+/g, ''))
        .filter(Boolean);
    if (!parts.length) return 'N/A';
    return `Phase ${parts.join('/')}`;
};

const ClinicalTrialsCard = ({ trialsData }) => {
    if (!trialsData) return null;

    // Support either decision_support.clinical_trials or VEP custom annotations
    const activeTrials = trialsData.active_trials || trialsData.trials || [];
    const totalCount = trialsData.total_count ?? activeTrials.length;
    const query = trialsData.search_query || 'N/A';

    if (!Array.isArray(activeTrials) || activeTrials.length === 0) return null;

    const recruitingCount = activeTrials.filter((t) => (t.status || '').toUpperCase() === 'RECRUITING').length;

    return (
        <ONCRP_TOOLS_QP_Card title="Clinical Trials" icon="fa-flask" className="ONCRP-TOOLS-QP-span-col-6">
            <div className="ONCRP-TOOLS-QP-subsection">
                <h4>Overview</h4>
                <div className="ONCRP-TOOLS-QP-kv-grid five-column-grid">
                    <KeyValue label="Search Query">{query}</KeyValue>
                    <KeyValue label="Total Trials">{totalCount}</KeyValue>
                    <KeyValue label="Showing">{activeTrials.length}</KeyValue>
                    <KeyValue label="Recruiting">{recruitingCount}</KeyValue>
                    <KeyValue label="Source">ClinicalTrials.gov</KeyValue>
                </div>
            </div>

            <div className="ONCRP-TOOLS-QP-section">
                <h4>Top Relevant Trials</h4>
                <div className="ONCRP-TOOLS-QP-table-container">
                    <table>
                        <thead>
                            <tr>
                                <th>Phase</th>
                                <th>Title</th>
                                <th>Status</th>
                                <th>Locations</th>
                                <th>Interventions</th>
                                <th>Sponsor</th>
                                <th>Relevance</th>
                            </tr>
                        </thead>
                        <tbody>
                            {activeTrials.map((t, i) => {
                                const nctId = t.nctId || '';
                                const firstLoc = Array.isArray(t.locations) && t.locations.length > 0 ? t.locations[0] : null;
                                const extraLocs = Array.isArray(t.locations) && t.locations.length > 1 ? t.locations.length - 1 : 0;
                                const locText = firstLoc ? `${firstLoc.city || ''}${firstLoc.city && firstLoc.country ? ', ' : ''}${firstLoc.country || ''}${extraLocs ? ` (+${extraLocs} more)` : ''}` : '—';
                                const title = t.title || nctId || 'N/A';
                                const interventions = Array.isArray(t.interventions) ? t.interventions.join(' + ') : (t.interventions || '—');
                                const sponsor = t.leadSponsor || '—';
                                const rel = (t.relevanceScore ?? '') !== '' ? t.relevanceScore : '—';
                                return (
                                    <tr key={nctId || i}>
                                        <td>{prettyPhase(t.phase)}</td>
                                        <td>
                                            {nctId ? (
                                                <a href={`https://clinicaltrials.gov/study/${nctId}`} target="_blank" rel="noopener noreferrer">{title}</a>
                                            ) : (
                                                title
                                            )}
                                        </td>
                                        <td><span className="ONCRP-TOOLS-QP-tag">{toTitle(t.status)}</span></td>
                                        <td>{locText}</td>
                                        <td>{interventions}</td>
                                        <td>{sponsor}</td>
                                        <td>{rel}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </ONCRP_TOOLS_QP_Card>
    );
};

export default ClinicalTrialsCard;

